<script setup lang="ts">
/**
 * Нижняя навигация телефона — макеты 27:2 и 28:107: четыре вкладки, активная
 * подсвечена основным цветом.
 *
 * Сайдбар на телефоне не помещается, а без навигации экран становится
 * тупиком. «Поиск» открывает ту же палитру, что Cmd+K на десктопе: другого
 * поля поиска у трекера нет.
 */
import { Book, CheckSquare, Columns3, Search } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { openPalette } from '@/components/palette'
import { useNavigationStore } from '@/stores/navigation.store'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const navigation = useNavigationStore()

/** Доска — последнего открытого проекта, иначе первого из списка. */
const boardKey = computed(
  () => String(route.params.key ?? '') || navigation.projects[0]?.key || '',
)

const isBoard = computed(() => route.path.includes('/board'))
const isMy = computed(() => route.path.startsWith('/my'))
const isKb = computed(() => route.path.startsWith('/kb'))

const goBoard = () => {
  if (boardKey.value) void router.push(`/p/${boardKey.value}/board`)
  else void router.push('/projects')
}
</script>

<template>
  <nav class="tabbar" :aria-label="t('nav.mobile')">
    <button type="button" class="tabbar__item" :class="{ 'tabbar__item--active': isBoard }" @click="goBoard">
      <Columns3 :size="20" />
      <span>{{ t('views.board') }}</span>
    </button>

    <RouterLink class="tabbar__item" :class="{ 'tabbar__item--active': isMy }" to="/my">
      <CheckSquare :size="20" />
      <span>{{ t('nav.myTasksShort') }}</span>
    </RouterLink>

    <button type="button" class="tabbar__item" @click="openPalette()">
      <Search :size="20" />
      <span>{{ t('palette.tab') }}</span>
    </button>

    <RouterLink class="tabbar__item" :class="{ 'tabbar__item--active': isKb }" to="/kb">
      <Book :size="20" />
      <span>{{ t('nav.knowledgeShort') }}</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.tabbar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 40;
  display: flex;
  /* Полоса жеста iPhone заходит на нижние 34 пикселя — вкладки уезжают выше. */
  padding-bottom: env(safe-area-inset-bottom, 0);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
}

.tabbar__item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  /* Тач-цель 56: меньше 44 палец не попадает, а подпись просит места. */
  height: 56px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  text-decoration: none;
  cursor: pointer;
}

.tabbar__item--active {
  color: var(--color-primary);
}
</style>
